import {
	PARTNER_CALENDAR_STATUS_COLORS,
	PARTNER_CALENDAR_STATUS_LABELS,
	PartnerCalendarStatus,
} from "Partner/Components/Calendar/types";
import { Box, TableCell, Tooltip, Typography } from "@mui/material";
import type { PartnerAvailability } from "./types";

type DayCellProps = {
	availability?: PartnerAvailability;
	onClick?: () => void;
	isWeekend?: boolean;
	isToday?: boolean;
};

export function DayCell({
	availability,
	onClick,
	isWeekend,
	isToday,
}: DayCellProps) {
	// Single Source of Truth: Absence of availability record means "Not Available"
	const status = availability?.status ?? PartnerCalendarStatus.NOT_AVAILABLE;

	const bgcolor = PARTNER_CALENDAR_STATUS_COLORS[status];

	const color = "#ffffff"; // Always white text for contrast

	const bookings = availability?.bookings || [];
	const bookingsCount = bookings.length;
	const capacity = availability?.capacity || 1;

	const label =
		status === PartnerCalendarStatus.ADAM
			? "Job"
			: PARTNER_CALENDAR_STATUS_LABELS[status];

	const content = (
		<Typography
			variant="caption"
			color={color}
			fontWeight={600}
			sx={{ fontSize: "0.65rem" }}
		>
			{label}
		</Typography>
	);

	const tooltipContent = availability ? (
		<Box sx={{ p: 1 }}>
			<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
				{PARTNER_CALENDAR_STATUS_LABELS[status]}
			</Typography>
			{bookingsCount > 0 && (
				<Typography variant="caption" display="block" sx={{ mb: 1 }}>
					Capacity: {bookingsCount} / {capacity}
				</Typography>
			)}
			{bookings.map((b) => (
				<Box
					key={b.orderId}
					sx={{
						mb: 0.5,
						p: 0.5,
						bgcolor: "rgba(255,255,255,0.1)",
						borderRadius: 1,
					}}
				>
					<Typography variant="caption" display="block" fontWeight="bold">
						Order #{b.orderId}
					</Typography>
					<Typography variant="caption" display="block">
						Day {b.dayOfJob} of {b.duration}
					</Typography>
				</Box>
			))}
		</Box>
	) : (
		<Typography variant="caption">Not Available</Typography>
	);

	return (
		<Tooltip title={tooltipContent} arrow placement="top">
			<TableCell
				align="center"
				sx={{
					p: 0.5,
					borderRight: "1px dashed #e5e7eb", // Softer dashed line
					borderLeft: isToday ? "2px solid" : undefined,
					borderLeftColor: isToday ? "primary.main" : undefined,
					borderRightColor: isToday ? "primary.main" : undefined,
					borderRightStyle: isToday ? "solid" : "dashed",
					borderRightWidth: isToday ? "2px" : "1px",
					height: 60,
					minWidth: 50,
					backgroundColor: isWeekend ? "action.hover" : "inherit",
				}}
			>
				<Box
					onClick={onClick}
					sx={{
						width: 40,
						height: 40,
						margin: "0 auto", // Center horizontally
						bgcolor,
						borderRadius: 1, // Slightly less rounded for a "square" feel
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: onClick ? "pointer" : "default",
						transition: "all 0.2s ease",
						"&:hover": {
							// Scale if interactive or if it represents an actual data record
							transform: onClick || availability ? "scale(1.05)" : "none",
							boxShadow: onClick || availability ? 2 : 0, // Subtle lift
							opacity: onClick ? 0.8 : 1,
						},
					}}
				>
					{content}
				</Box>
			</TableCell>
		</Tooltip>
	);
}
